// ═══════════════════════════════════════════════════════════════
// PIXALLOY — LeftPanel Render Function
// ═══════════════════════════════════════════════════════════════
// Navigator, Histogram, Adjustments, Filters, Color, History, Info.
// Called inline from PixAlloy.jsx as renderLeftPanel(p).
// ═══════════════════════════════════════════════════════════════

import { V } from "../theme.js";
import { C, T, ADJ, ADJ_LABELS, SWATCHES, FILTERS } from "../engine/index.js";
import { hx, rgbHex, rgbToHsl, PanelSection, InfoCell, AdjRow, LayerThumb, OptBtn } from "../ui/components.jsx";

export function renderLeftPanel(p) {
  const { leftCollapsed, setLeftCollapsed, leftW, startResize,
    navCvs, histCvs, histCh, setHistCh, waveCvs, showWave, setShowWave,
    collapsedSections, toggleSection,
    E, layers, actIdx, pc, fu,
    setAdjEditIdx, setAdjEditType, setShowAdjDialog,
    setTool, setBcol,
    cw, ch, curPos, curRGB, hslVal, bcol, bgCol,
    histJumpIdx, setHistJumpIdx, showClip, setShowClip,
    bright, setBright, contrast, setContrast, sat, setSat,
    history, zmDisplay, panState, applyVPTransform, zoomToFit,
    setShowLevels } = p;
  return (<>
        {!leftCollapsed?(<>
        <div style={{width:leftW,background:V.panelBg,borderRight:`1px solid ${V.panelBorder}`,display:"flex",flexDirection:"column",flexShrink:0,overflowY:"auto",overflowX:"hidden"}}>

          {/* Navigator */}
          <PanelSection title="Navigator" collapsed={collapsedSections.nav} toggle={()=>toggleSection("nav")}>
            <div style={{position:"relative"}}>
              <canvas ref={navCvs} width={196} height={120} style={{width:"100%",height:"auto",display:"block",borderRadius:3,border:`1px solid ${V.panelBorder2}`}}/>
              <div style={{position:"absolute",border:`1px solid ${V.accent}`,boxShadow:`0 0 0 1px rgba(190,27,27,0.3)`,top:"10%",left:"10%",width:"80%",height:"80%",pointerEvents:"none"}}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}>
              <OptBtn onClick={()=>{panState.current.zoom=Math.max(panState.current.zoom/1.25,0.1);applyVPTransform();}} style={{flex:1,textAlign:"center"}}>−</OptBtn>
              <span style={{fontSize:10,color:V.muted,padding:"0 4px"}}>{zmDisplay}%</span>
              <OptBtn onClick={()=>{panState.current.zoom=Math.min(panState.current.zoom*1.25,8);applyVPTransform();}} style={{flex:1,textAlign:"center"}}>+</OptBtn>
              <OptBtn onClick={zoomToFit} style={{marginLeft:4,textAlign:"center"}} title="Zoom to Fit (Ctrl+0)">⊞</OptBtn>
            </div>
          </PanelSection>

          {/* Histogram */}
          <PanelSection title="Histogram" collapsed={collapsedSections.hist} toggle={()=>toggleSection("hist")}>
            <div style={{display:"flex",gap:4,marginBottom:6}}>
              {[{k:"rgb",l:"RGB",c:V.muted},{k:"r",l:"R",c:"#f87171"},{k:"g",l:"G",c:"#4ade80"},{k:"b",l:"B",c:"#60a5fa"},{k:"lum",l:"Lum",c:"#aaa"}].map(ch2=>(
                <span key={ch2.k} onClick={()=>setHistCh(ch2.k)}
                  style={{fontSize:9,padding:"2px 7px",borderRadius:3,cursor:"pointer",fontFamily:V.mono,letterSpacing:"0.5px",color:ch2.c,border:histCh===ch2.k?`1px solid ${ch2.c}`:"1px solid transparent",background:histCh===ch2.k?`${ch2.c}22`:"transparent",transition:"all 0.1s"}}>{ch2.l}</span>
              ))}
            </div>
            <canvas ref={histCvs} width={196} height={56} style={{width:"100%",borderRadius:3}}/>
            <div style={{display:"flex",gap:4,marginTop:4}}>
              <OptBtn onClick={()=>setShowClip(v=>!v)} style={{flex:1,textAlign:"center",fontSize:9,color:showClip?V.accentL:V.muted,borderColor:showClip?V.accent:V.panelBorder2}}>Clipping</OptBtn>
              <OptBtn onClick={()=>setShowWave(v=>!v)} style={{flex:1,textAlign:"center",fontSize:9,color:showWave?"#4ade80":V.muted,borderColor:showWave?"#4ade80":V.panelBorder2}}>Waveform</OptBtn>
            </div>
            {showWave&&<canvas ref={waveCvs} width={196} height={70} style={{width:"100%",borderRadius:3,marginTop:4,border:`1px solid ${V.panelBorder2}`}}/>}
          </PanelSection>

          {/* Adjustments */}
          <PanelSection title="Adjustments" collapsed={collapsedSections.adj} toggle={()=>toggleSection("adj")}>
            <AdjRow label="Brightness" value={bright} id="vBright" min={-100} max={100} onChange={setBright}/>
            <AdjRow label="Contrast" value={contrast} id="vContrast" min={-100} max={100} onChange={setContrast}/>
            <AdjRow label="Saturation" value={sat} id="vSat" min={-100} max={100} onChange={setSat}/>
            <div style={{marginTop:4}}>
              <OptBtn onClick={()=>{setBright(0);setContrast(0);setSat(0);}} style={{width:"100%",textAlign:"center"}}>Reset</OptBtn>
            </div>
            <div style={{marginTop:6}}>
              <OptBtn onClick={()=>setShowLevels(true)} style={{width:"100%",textAlign:"center"}}>Levels…</OptBtn>
            </div>
            <div style={{marginTop:4}}>
              <OptBtn onClick={()=>{setAdjEditIdx(null);setAdjEditType(ADJ.HUESAT);setShowAdjDialog(true);}} style={{width:"100%",textAlign:"center"}}>⊕ Hue/Sat Layer…</OptBtn>
            </div>
          </PanelSection>

          {/* Filters */}
          <PanelSection title="Quick Filters" collapsed={collapsedSections.filters} toggle={()=>toggleSection("filters")}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
              {FILTERS.map(f=>(
                <div key={f.id} onClick={()=>{pc(C.FILTER,{id:f.id});fu();}}
                  style={{fontSize:10,padding:"5px 6px",textAlign:"center",background:V.panelActive,border:`1px solid ${V.panelBorder2}`,borderRadius:3,color:V.muted,cursor:"pointer",fontFamily:V.mono,transition:"all 0.12s"}}
                  onMouseEnter={e=>{e.currentTarget.style.background=V.panelHover;e.currentTarget.style.color=V.text;e.currentTarget.style.borderColor="#555";}}
                  onMouseLeave={e=>{e.currentTarget.style.background=V.panelActive;e.currentTarget.style.color=V.muted;e.currentTarget.style.borderColor=V.panelBorder2;}}>{f.l}</div>
              ))}
            </div>
          </PanelSection>

          {/* Color */}
          <PanelSection title="Color" collapsed={collapsedSections.color} toggle={()=>toggleSection("color")}>
            <div style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
              <div style={{position:"relative"}}>
                <div style={{width:28,height:28,borderRadius:3,background:bcol,border:`1px solid ${V.panelBorder2}`,cursor:"pointer"}}
                  onClick={()=>document.getElementById("ember-color-pick")?.click()}/>
                <input id="ember-color-pick" type="color" value={bcol} onChange={e=>setBcol(e.target.value)} style={{position:"absolute",top:0,left:0,width:28,height:28,opacity:0,cursor:"pointer"}}/>
              </div>
              <span style={{fontSize:9,color:V.ghost}}>⇄</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:4,marginBottom:6}}>
              {[{l:"R",c:"#f87171",v:hx(bcol).r,set:v=>{const c=hx(bcol);setBcol(rgbHex(Math.max(0,Math.min(255,+v)),c.g,c.b));}},
                {l:"G",c:"#4ade80",v:hx(bcol).g,set:v=>{const c=hx(bcol);setBcol(rgbHex(c.r,Math.max(0,Math.min(255,+v)),c.b));}},
                {l:"B",c:"#60a5fa",v:hx(bcol).b,set:v=>{const c=hx(bcol);setBcol(rgbHex(c.r,c.g,Math.max(0,Math.min(255,+v))));}}
              ].map(ch2=>(
                <div key={ch2.l} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <span style={{fontSize:9,color:ch2.c,letterSpacing:"0.5px"}}>{ch2.l}</span>
                  <input type="number" value={ch2.v} min={0} max={255} onChange={e=>ch2.set(e.target.value)}
                    style={{width:"100%",background:V.panelActive,border:`1px solid ${V.panelBorder2}`,color:V.text,fontFamily:V.mono,fontSize:10,padding:"3px 4px",borderRadius:3,textAlign:"center",outline:"none"}}/>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:4,alignItems:"center",marginBottom:6}}>
              <span style={{fontSize:10,color:V.ghost}}>#</span>
              <input type="text" value={bcol.slice(1)} maxLength={6} onChange={e=>{const v=e.target.value;if(/^[0-9a-fA-F]{6}$/.test(v))setBcol("#"+v);}}
                style={{flex:1,background:V.panelActive,border:`1px solid ${V.panelBorder2}`,color:V.text,fontFamily:V.mono,fontSize:10,padding:"3px 6px",borderRadius:3,outline:"none"}}/>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
              {SWATCHES.map(c=>(
                <div key={c} onClick={()=>setBcol(c)}
                  style={{width:16,height:16,borderRadius:2,background:c,cursor:"pointer",border:"1px solid rgba(255,255,255,0.08)",transition:"transform 0.1s"}}
                  onMouseEnter={e=>e.currentTarget.style.transform="scale(1.2)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}/>
              ))}
            </div>
          </PanelSection>

          {/* History */}
          <PanelSection title="History" collapsed={collapsedSections.history} toggle={()=>toggleSection("history")} flex>
            <div style={{overflowY:"auto",maxHeight:180}}>
              {history.map((h,i)=>{
                const undoLen=E?.s.undo.length??0;
                const redoLen=E?.s.redo.length??0;
                const currentIdx=undoLen; // Current state is at undo stack top
                const isCurrent=i===history.length-1;
                const isReachable=i<history.length;
                const isFuture=i>currentIdx&&!isCurrent;
                return (
                <div key={i} onClick={()=>{
                  if(isCurrent)return;
                  if(i<currentIdx){
                    // Go back: undo multiple times
                    const steps=currentIdx-i;
                    for(let s2=0;s2<steps;s2++)pc(C.UNDO);
                    fu();
                  } else if(i>currentIdx){
                    // Go forward: redo multiple times
                    const steps=i-currentIdx;
                    for(let s2=0;s2<steps;s2++)pc(C.REDO);
                    fu();
                  }
                }}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"4px 6px",borderRadius:3,fontSize:10,
                    color:isCurrent?V.text:isFuture?"#555":V.muted,
                    cursor:isCurrent?"default":"pointer",transition:"background 0.1s",
                    opacity:isCurrent?1:isFuture?0.4:0.8}}
                  onMouseEnter={e=>e.currentTarget.style.background=V.panelHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  {isCurrent&&<span style={{color:V.accent,fontSize:8,flexShrink:0}}>▸</span>}
                  {!isCurrent&&<span style={{width:7,flexShrink:0}}/>}
                  <span style={{fontSize:11,flexShrink:0}}>{h.icon}</span>
                  <span>{h.label}</span>
                  <span style={{marginLeft:"auto",color:V.ghost,fontSize:9}}>{String(i+1).padStart(2,"0")}</span>
                </div>
              );})}
            </div>
          </PanelSection>
          {/* Info */}
          <PanelSection title="Info" collapsed={collapsedSections.info} toggle={()=>toggleSection("info")}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
              <InfoCell label="R / G / B" val={`${curRGB.r} / ${curRGB.g} / ${curRGB.b}`}/>
              <InfoCell label="H / S / L" val={`${hslVal[0]}° / ${hslVal[1]}% / ${hslVal[2]}%`}/>
              <InfoCell label="X / Y" val={`${Math.round(curPos.x)} / ${Math.round(curPos.y)}`}/>
              <InfoCell label="W / H" val={`${cw} × ${ch}`}/>
            </div>
          </PanelSection>
        </div>
        <div className="rh" onMouseDown={e=>startResize("left",e)} onDoubleClick={()=>setLeftCollapsed(true)}/>
        </>):<div style={{width:6,background:V.panelBg,cursor:"pointer",borderRight:`1px solid ${V.panelBorder}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}} onClick={()=>setLeftCollapsed(false)}><span style={{color:V.ghost,fontSize:10,writingMode:"vertical-lr"}}>◂ L</span></div>}

  </>);
}
